import * as React from "react";
import { Grid } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import {
  BktValidatedTextField,
  BktTypeaheadSelect,
  ISelectData,
  BktSelect,
  BktValidatedSelectChipField
} from "front-end-lib/core";
import { MddCheckbox } from "../../../components";
import {
  IStudyQualification,
  StudyQualification,
  IStudyScale,
  ILearningPlan,
  ISelectChangeEvent,
  Validations
} from "../../../model";
import { ILookup } from "../../../model/interfaces/iLookup";
import { MAX_TEXT_LENGTH } from "../../../constants";
import { QUALIFICATION_TYPE } from "../../../constants/applicationConstants";
import { isLoading } from "../../../utils";
import { eStudySaga } from "../../../sagas";

interface IMddStudyQualificationAddEditContentProps {
  qual: IStudyQualification;
  initialStudyQuals: React.RefObject<IStudyQualification[]>;
  setStudyQualification: (studyQualification: IStudyQualification) => void;
  qualificationTypes: ILookup[];
  scales: IStudyScale[];
  learningPlans: ILearningPlan[];
  mode: "Add" | "Edit";
  loadingSave: boolean;
  loadingQualPlans: boolean;
}

// Define the interface for ILookupNameValue since it's not exported from front-end-lib
interface ILookupNameValue {
  value: string;
  label: string;
}

export const MddStudyQualificationAddEditContent = ({
  qual,
  initialStudyQuals,
  setStudyQualification,
  qualificationTypes,
  scales,
  learningPlans,
  mode,
  loadingSave,
  loadingQualPlans
}: IMddStudyQualificationAddEditContentProps) => {
  const qualRef = React.useRef(qual);
  qualRef.current = qual;

  const findLearningPlanById = (id: string) => {
    return learningPlans.find((lp) => lp.id === id);
  };

  const handleLearningPlanChange = (
    event: React.ChangeEvent<ISelectChangeEvent>
  ) => {
    // Cast as any is necessary because the value is actually a string array
    const selectedPlans = (event.target.value as any).map((lp: string) => {
      return findLearningPlanById(lp);
    });

    setStudyQualification(
      Object.assign(new StudyQualification(), qual, {
        LearningPlans: selectedPlans
      })
    );
  };

  const isExperienceDisabled = React.useCallback(
    (qualType: string) => {
      let isScaleSelected = false;
      if (mode === "Edit") {
        if (qualRef.current.ScaleName && qualRef.current.ScaleName.length) {
          isScaleSelected = true;
        }
      } else {
        isScaleSelected =
          qualRef.current.ScaleId !== undefined &&
          qualRef.current.ScaleId.length > 0;
      }

      if (
        (qualType && qualType !== QUALIFICATION_TYPE.TRAINING) ||
        !isScaleSelected
      ) {
        return true;
      }
      return false;
    },
    [mode]
  );

  const isAnotherScalePrimary = React.useCallback(() => {
    // Only check if we have quals to work with and a scale selected
    if (
      !qualRef.current.ScaleId ||
      !initialStudyQuals ||
      !initialStudyQuals.current
    ) {
      return false;
    }

    // Look through initial qualifications to find if any other scale
    // is already set as primary
    return initialStudyQuals.current.some(
      (q) =>
        q.IsPrimary &&
        q.ScaleId === qualRef.current.ScaleId &&
        q.StudyQualificationId !== qualRef.current.StudyQualificationId
    );
  }, [initialStudyQuals]);

  const isPrimaryQualDisabled = React.useCallback(
    (qualType: string) => {
      let isScaleSelected = false;
      if (mode === "Edit") {
        if (qualRef.current.ScaleName && qualRef.current.ScaleName.length) {
          isScaleSelected = true;
        }
      } else {
        isScaleSelected =
          qualRef.current.ScaleId !== undefined &&
          qualRef.current.ScaleId.length > 0;
      }

      // First, check qualification type and scale selection, also check if another qualification on the same scale is already set as primary
      if (
        (qualType && qualType !== QUALIFICATION_TYPE.TRAINING) ||
        !isScaleSelected ||
        isAnotherScalePrimary()
      ) {
        return true;
      }

      return false;
    },
    [isAnotherScalePrimary, mode]
  );

  const isScaleDisabled = React.useCallback(() => {
    if (
      mode === "Edit" ||
      scales.length === 0 ||
      loadingSave ||
      qualRef.current.QualificationType === QUALIFICATION_TYPE.RATER_EXPERIENCE
    ) {
      return true;
    }
    return false;
  }, [mode, scales.length, loadingSave]);

  const isLearningPlanDisabled = React.useCallback(() => {
    if (mode === "Edit" || mode === "Add") {
      if (
        loadingQualPlans ||
        !qual ||
        !learningPlans ||
        !learningPlans.length
      ) {
        return true;
      }
    }
    return false;
  }, [mode, loadingQualPlans, qual, learningPlans]);

  const handleQualTypeChange = (val: string) => {
    const isRaterExperience = val === QUALIFICATION_TYPE.RATER_EXPERIENCE;
    setStudyQualification(
      Object.assign(new StudyQualification(), qualRef.current, {
        QualificationType: val,
        // blank out scale ID and name if RES or RIS
        ScaleId: isRaterExperience ? "" : qualRef.current.ScaleId,
        ScaleName: isRaterExperience ? "" : qualRef.current.ScaleName,
        IsExperienceRequired: isExperienceDisabled(val)
          ? false
          : qualRef.current.IsExperienceRequired,
        IsPrimary: isPrimaryQualDisabled(val)
          ? false
          : qualRef.current.IsPrimary
      })
    );
    // This is a workaround, the front-end-lib sets the value of this field based on
    // the handleTextChange function which won't get called with a manual assignment
    // of the value in state above.  Until we can fix that this will clear out the field.
    if (isRaterExperience) {
      const txtScale = document.getElementById(
        "txtScaleName"
      ) as HTMLInputElement;
      txtScale.value = "";
    }
  };

  const getDisplayScaleName = () => {
    return mode === "Edit"
      ? qual.ScaleName
        ? qual.ScaleName
        : ""
      : qual.ScaleId;
  };

  const loadingStudyScales = isLoading(
    `undefined_${eStudySaga.FETCH_STUDY_SCALES}`
  );

  const scaleSelectData = React.useMemo(
    () =>
      scales
        ? scales.map((sc: IStudyScale) => ({
            value: sc.ScaleId,
            label: sc.Name.trim()
          }))
        : [],
    [scales]
  );

  const learningPlanData = React.useMemo(
    () =>
      !learningPlans
        ? []
        : (learningPlans.map((lp: ILearningPlan) => {
            return {
              value: lp.id,
              label: lp.name
            } as ISelectData;
          }) as ISelectData[]),
    [learningPlans]
  );

  return (
    <Grid container spacing={10} key="grid">
      <Grid item xs={6} className="mdd-qualification--content--grid">
        <div
          className={
            mode === "Edit"
              ? "mdd-qualification--disabled-text mdd-qualification--content--header"
              : "mdd-qualification--content--header"
          }
        >
          Qualification Type
        </div>
        <BktSelect
          key={`key_abc`}
          menuProps={{}}
          textProps={{
            id: "qualType",
            label: "",
            value: qual.QualificationType,
            className: "mdd-qualification--content--select",
            onChange: (event) => handleQualTypeChange(event.target.value),
            disabled: mode === "Edit"
          }}
          selectData={qualificationTypes}
          selectMenuProps={{
            className: "mdd-qualification--content--select--div"
          }}
        />
        <div
          className={
            mode === "Edit"
              ? "mdd-qualification--disabled-text mdd-qualification--content--header-padded"
              : "mdd-qualification--content--header-padded"
          }
        >
          Scale
        </div>
        <BktTypeaheadSelect
          selectData={scaleSelectData}
          key="txtEditScaleName"
          textProps={{
            textProps: {
              label: "",
              className: "mdd-qualification--content--select",
              InputLabelProps: { shrink: true },
              inputProps: {
                className: "mdd-qualification--content--input",
                id: "txtScaleName"
              },
              placeholder: !qual.ScaleName
                ? loadingStudyScales
                  ? "Loading Scales..."
                  : "Start typing to select a scale."
                : "",
              disabled: isScaleDisabled(),
              value: getDisplayScaleName()
            }
          }}
          itemProps={{ name: "ScaleName" }}
          onSubmit={(e: ILookupNameValue) => {
            setStudyQualification(
              Object.assign(new StudyQualification(), qual, {
                ScaleName: e.value ? e.label : undefined,
                ScaleId: e.value,
                // Clear out experience required if they blanked out the scale
                IsExperienceRequired: e.value
                  ? qual.IsExperienceRequired
                  : false,
                // Also reset IsPrimary when scale is cleared
                IsPrimary: e.value ? qual.IsPrimary : false
              })
            );
          }}
        />
        <MddCheckbox
          lblText="Experience Required"
          lblProps={{
            className: isExperienceDisabled(qualRef.current.QualificationType)
              ? "mdd-qualification--disabled-text"
              : ""
          }}
          chkBoxProps={{
            id: "chkStudyQualificationExperienceReq",
            checked: qual.IsExperienceRequired ? true : false,
            value: "chkExpRequired",
            disabled: isExperienceDisabled(qualRef.current.QualificationType),
            onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
              setStudyQualification(
                Object.assign(new StudyQualification(), qual, {
                  IsExperienceRequired: !qual.IsExperienceRequired
                })
              )
          }}
        />
        {mode !== "Add" && (
          <MddCheckbox
            lblText="Qualification Active"
            chkBoxProps={{
              id: "chkStudyQualifcationActive",
              checked: qual.IsActive,
              value: "chkActive",
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setStudyQualification(
                  Object.assign(new StudyQualification(), qual, {
                    IsActive: !qual.IsActive
                  })
                )
            }}
          />
        )}
        <span>
          <MddCheckbox
            lblText="Primary Study Qualification"
            lblProps={{
              className: isPrimaryQualDisabled(
                qualRef.current.QualificationType
              )
                ? "mdd-qualification--disabled-text"
                : ""
            }}
            chkBoxProps={{
              id: "chkStudyQualificationPrimary",
              checked: qual.IsPrimary,
              value: "chkPrimary",
              disabled: isPrimaryQualDisabled(
                qualRef.current.QualificationType
              ),
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setStudyQualification(
                  Object.assign(new StudyQualification(), qual, {
                    IsPrimary: !qual.IsPrimary
                  })
                )
            }}
          />
          {isAnotherScalePrimary() && (
            <span
              className="mdd-qualification--info-icon"
              id="iconInfo"
              data-testid="iconInfo"
              title="Primary Study Qualification for selected scale exists"
            >
              <InfoIcon fontSize="small" />
            </span>
          )}
        </span>
      </Grid>
      <Grid
        item
        xs={6}
        className="mdd-qualification--content--grid-with-border"
      >
        <div
          className="mdd-qualification--content--header"
          data-testid="qualNameLabel"
        >
          Qualification Name *
        </div>
        <BktValidatedTextField
          key="txtEditQualification"
          validationForOnBlur={[Validations.required]}
          validationForOnChange={[Validations.valid]}
          textFieldProps={{
            debounceInterval: 0,
            textProps: {
              id: "txtEditQualification",
              label: "",
              className: "mdd-qualification--content--select",
              InputLabelProps: { shrink: true },
              inputProps: {
                maxLength: MAX_TEXT_LENGTH.QUALIFICATION_NAME,
                className: "mdd-qualification--outlined-input"
              },
              placeholder: "Enter Qualification Name",
              disabled: loadingSave,
              variant: "outlined",
              onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                setStudyQualification(
                  Object.assign(new StudyQualification(), qual, {
                    QualificationName: e.target.value
                  })
                ),
              value: qual.QualificationName
            }
          }}
        />
        <div className="mdd-qualification--content--header-padded">
          Learning Plans
        </div>
        <BktValidatedSelectChipField
          key="fldLearningPlans"
          id={"mdd-qualification--select-multiple-chip"}
          name={"learningPlans"}
          inputLabelText={
            loadingQualPlans || learningPlanData.length
              ? ""
              : "No available Learning Plans"
          }
          data={learningPlanData || undefined}
          selectProps={{
            disabled: isLearningPlanDisabled(),
            className:
              "validated-selectchip-field__select mdd-qualification--content--select--div"
          }}
          value={
            !qual.LearningPlans
              ? []
              : qual.LearningPlans.map((lp: ILearningPlan) => {
                  return lp.id;
                })
          }
          onChange={handleLearningPlanChange}
          frmControlProps={{
            required: false,
            "aria-labelledby": "learningPlans"
          }}
        />
        {qual!.LegacyLearningPlans!.length > 0 && (
          <React.Fragment>
            <div className="mdd-qualification--content--legacy-LP-info--label">
              Legacy Learning Plan
            </div>
            <div className="mdd-qualification--content--legacy-LP-info--text">
              {qual!.LegacyLearningPlans![0].name}
            </div>
          </React.Fragment>
        )}
      </Grid>
    </Grid>
  );
};
